import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  UserIcon, 
  BriefcaseIcon, 
  PlusIcon, 
  DocumentTextIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

// 安全な文字列化（オブジェクトや配列が来ても落ちないように）
const safeText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch (_) { return String(v); }
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const isEmployerDashboard = location.pathname.startsWith('/employer-dashboard') || location.pathname.startsWith('/business/dashboard');
  const isSeekerDashboard = location.pathname.startsWith('/seeker-dashboard') || location.pathname.startsWith('/mypage');

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {/* Back to Dashboard: Employer */}
            {user && user.user_type === 'employer' && !isEmployerDashboard && (
              <button
                onClick={() => navigate('/employer-dashboard')}
                className="mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors clickable"
                title="ダッシュボードに戻る"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
              </button>
            )}
            {/* Back to Dashboard/MyPage: Job Seeker */}
            {user && user.user_type === 'job_seeker' && !isSeekerDashboard && (
              <button
                onClick={() => navigate('/seeker-dashboard')}
                className="mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors clickable"
                title="ダッシュボードに戻る"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
              </button>
            )}
            <Link to="/" className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-blue-600">byters</h1>
              </div>
              <div className="ml-2 text-sm text-gray-500">
                即日バイトマッチング
              </div>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {/* 事業者以外、またはログインしていない場合の求人一覧表示 */}
            {(!user || user.user_type !== 'employer') && (
              <Link
                to="/jobs"
                className={`nav-link flex items-center ${isActive('/jobs') ? 'active' : ''}`}
              >
                <BriefcaseIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                <span className="hidden sm:inline whitespace-nowrap overflow-hidden text-ellipsis">求人一覧</span>
                <span className="sm:hidden whitespace-nowrap overflow-hidden text-ellipsis">求人</span>
              </Link>
            )}

            {user ? (
              <>
                {user.user_type === 'employer' ? (
                  // 事業者の場合：ログアウトボタンのみ表示
                  <button
                    onClick={handleLogout}
                    className="nav-link flex items-center text-red-600 hover:text-red-700"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4 mr-1" />
                    ログアウト
                  </button>
                ) : (
                  // 求職者の場合：従来通りのナビゲーション
                  <>
                    <Link
                      to="/mypage"
                      className={`nav-link flex items-center ${isActive('/mypage') ? 'active' : ''}`}
                    >
                      <UserIcon className="w-4 h-4 mr-1" />
                      {safeText(user.name)}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="nav-link flex items-center text-red-600 hover:text-red-700"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4 mr-1" />
                      ログアウト
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-gray-700 hover:text-blue-600 font-medium text-sm md:text-base transition-colors ${isActive('/login') ? 'text-blue-600' : ''}`}
                >
                  ログイン
                </Link>
                <Link
                  to="/register/type"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm md:text-base transition-colors"
                >
                  新規登録
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;